import * as React from "react";
import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Slide,
  TextField,
  Theme,
  withStyles,
  Tooltip,
} from "@material-ui/core";
import { API } from "aws-amplify";
import { TransitionProps } from "@material-ui/core/transitions";
import { LoadingSpinner } from "../../../../../../utils/LoadingSpinner";
import CreateSubscriptionResult from "./CreateSubscriptionResult";
import { Alert } from "@material-ui/lab";

const styles = ({ spacing }: Theme) =>
  createStyles({
    buttonWrapper: {
      backgroundColor: "#00cc00",
      marginTop: spacing(1),
    },
  });

interface ICreateSubscriptionState {
  customerId: any;
  formSubmitted: boolean;
  message: string;
  open: boolean;
  personId: string;
  billingAddressId: string;
  resultSuccessful: boolean;
  showLoadingSpinner: boolean;
  response: string;
}

const TransitionConst = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

class CreateSubscriptionSection extends React.Component<
  any,
  ICreateSubscriptionState
> {
  constructor(props: any) {
    super(props);
    this.state = {
      billingAddressId: "",
      customerId: "",
      formSubmitted: false,
      message: "",
      open: false,
      personId: "",
      response: "",
      resultSuccessful: false,
      showLoadingSpinner: false,
    };

    this.handlePersonId = this.handlePersonId.bind(this);
    this.handleValidation = this.handleValidation.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleBillingAddressId = this.handleBillingAddressId.bind(this);
    this.handleCustomerId = this.handleCustomerId.bind(this);
  }

  public render() {
    // @ts-ignore
    return (
      <Grid item xs={12}>
        <Grid
          container
          direction="column"
          justify="center"
          alignItems="center"
          spacing={3}
        >
          {this.state.showLoadingSpinner && <LoadingSpinner />}

          <Alert severity="info">
            <p>
              Before triggering subscription, place an order with AP customer
              and generate the obfuscated billing address id. —{" "}
              <a
                href="https://w.amazon.com/bin/view/Pharmacy/Public/Teams/APAQ/Automation/TestTools/Portal/BillingAddressIdCreation/"
                target="_blank"
                rel="noopener noreferrer"
              >
                check it out!
              </a>
            </p>
          </Alert>

          <Grid item xs>
            <TextField
              style={{ width: 225 }}
              label="Customer Id (Required)"
              variant="outlined"
              value={this.state.customerId}
              onChange={this.handleCustomerId}
            />
          </Grid>

          <Grid item xs>
            <TextField
              style={{ width: 225 }}
              label="Person Id (Required)"
              variant="outlined"
              value={this.state.personId}
              onChange={this.handlePersonId}
            />
          </Grid>

          <Grid item xs>
            <Tooltip
              disableFocusListener
              title="Please use below steps to find the billingAddressId
                        - https://w.amazon.com/bin/view/Pharmacy/Public/Teams/APAQ/Automation/TestTools/Portal/BillingAddressIdCreation/"
            >
              <TextField
                style={{ width: 225 }}
                label="Billing Address Id (Required)"
                variant="outlined"
                value={this.state.billingAddressId}
                onChange={this.handleBillingAddressId}
              />
            </Tooltip>
          </Grid>

          <Grid item xs>
            <Button
              variant="outlined"
              color="default"
              onClick={this.handleSubmit}
            >
              Submit
            </Button>
          </Grid>

          <Grid item xs={12}>
            {this.state.formSubmitted && <LoadingSpinner />}
            {this.state.resultSuccessful && (
              <CreateSubscriptionResult
                resultSuccessful={this.state.resultSuccessful}
                response={this.state.response}
              />
            )}
          </Grid>

          {this.state.message && (
            <Dialog
              open={this.state.open}
              TransitionComponent={TransitionConst}
              keepMounted
              aria-labelledby="alert-dialog-slide-title"
              aria-describedby="alert-dialog-slide-description"
              onClose={this.handleClose}
            >
              <DialogTitle id="alert-dialog-slide-title">
                {"Exception occurred when creating subscription"}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-slide-description">
                  {this.state.message}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={this.handleClose} color="primary">
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </Grid>
      </Grid>
    );
  }

  public handleClose() {
    this.setState({ open: false });
  }

  public handlePersonId(event: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ personId: event.target.value });
  }

  public handleCustomerId(event: React.ChangeEvent<HTMLTextAreaElement>) {
    this.setState({ customerId: event.target.value });
  }

  public handleBillingAddressId(event: React.ChangeEvent<HTMLTextAreaElement>) {
    this.setState({ billingAddressId: event.target.value });
  }

  private handleValidation() {
    let formIsValid = true;
    if (!this.state.customerId) {
      alert("Please enter the customer id");
      formIsValid = false;
    } else if (!this.state.customerId.match("^A[A-Z0-9]*$")) {
      alert("Please enter the valid customer id.");
      formIsValid = false;
    }
    if (!this.state.personId) {
      alert("Please enter the person id");
      formIsValid = false;
    } else if (
      !this.state.personId.match(
        "^amzn[1-9]+[0-9]*.([A-Za-z0-9]+.)+A[A-Z0-9]*$"
      )
    ) {
      alert("Please enter the valid person id.");
      formIsValid = false;
    }
    if (!this.state.billingAddressId) {
      alert("Please enter the billing address id");
      formIsValid = false;
    } else if (!this.state.billingAddressId.match("^A[A-Z0-9]*$")) {
      alert("Please enter the valid obfuscated billing address id.");
      formIsValid = false;
    }
    return formIsValid;
  }

  private handleSubmit() {
    if (this.handleValidation()) {
      this.setState({ formSubmitted: true });
      this.setState({ resultSuccessful: false });

      const apiName = "ApaqPortalAPI";
      const path = "/pharmacy-customer/update/create-subscription";
      const config = {
        body: {
          billingAddressId: this.state.billingAddressId,
          customerId: this.state.customerId,
          personId: this.state.personId,
        },
        headers: {},
      };

      API.post(apiName, path, config)
        .then((response) => {
          console.log(response);
          this.setState({ resultSuccessful: true });
          this.setState({ formSubmitted: false });
          this.setState({
            response:
              'Subscription Successful - { "SubscriptionId":"' +
              response.subscriptionId +
              '" }',
          });
          this.setState({ personId: "" });
          this.setState({ customerId: "" });
          this.setState({ billingAddressId: "" });
        })
        .catch((error) => {
          console.log(error);
          this.setState({ open: true });
          this.setState({ formSubmitted: false });
          this.setState({ message: error.response.data.message });
        });
    }
  }
}

export default withStyles(styles)(CreateSubscriptionSection);
